import { camelCase } from 'lodash'
import * as Yup from 'yup'
import { Alert, Button, Col, Form, InputGroup, ListGroup, Modal, Row } from 'react-bootstrap'
import type { AlertState } from '../../common'

type RowValues = {
  id: number
  name: string
  pageMarginSettingType: string
  pageMarginOtherSettingId: number | null
}

type UpsertValues = {
  pageMarginSettingType: string
  pageMarginOtherSettingId: number | null | ''
  name: string
  order: number
}

type DeleteItem = {
  id: number
  pageMarginSettingType: string
  name: string
}

type SettingProp = {
  pageMarginOtherSettings?: Array<{ id: number; name: string }>
}

type EnumValues = {
  pageMarginSettingTypes: Record<string, string>
}

const _PageMarginSettingForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleSubmit,
  enumValues,
  isSettingWritable,
  selectedIds,
  dispatch,
  selectId,
  unselectId,
  setting,
  rowIndex
}: {
  values: RowValues | null
  errors: Partial<Record<keyof RowValues, string>>
  touched: Partial<Record<keyof RowValues, boolean>>
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleSubmit: (values: RowValues) => void
  enumValues: EnumValues
  isSettingWritable: boolean
  selectedIds: number[]
  dispatch: (action: unknown) => void
  selectId: (id: number) => unknown
  unselectId: (id: number) => unknown
  setting: SettingProp | null | undefined
  rowIndex: number
}) => {
  if (!values) {
    return <></>
  }
  const typeString = enumValues.pageMarginSettingTypes[camelCase(values?.pageMarginSettingType)]
  return (
    <Row sm='auto' className={`row-${rowIndex + 1}`}>
      <Col sm={2}>{typeString}</Col>
      <Col sm={2}>
        {values?.pageMarginSettingType === 'other'
          ? setting?.pageMarginOtherSettings?.find((s) => s.id === values?.pageMarginOtherSettingId)?.name || ''
          : '-'}
      </Col>
      <Col>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            id={`nameInput-${values.id}`}
            type='text'
            name='name'
            value={values.name || ''}
            disabled={!isSettingWritable}
            onChange={handleChange}
            onBlur={(_) => handleSubmit(values)}
            isInvalid={!!(touched.name && errors.name)}
          />
          <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
        </InputGroup>
      </Col>
      <Col sm='auto'>
        <Form.Check.Input
          type='checkbox'
          name='checkedGroups'
          value={values.id}
          checked={selectedIds?.includes(values.id)}
          onChange={(e) => dispatch((e.target.checked ? selectId : unselectId)(parseInt(e.target.value)))}
        />
      </Col>
    </Row>
  )
}

export const PageMarginSettingForm = Object.assign(_PageMarginSettingForm, {
  schema: Yup.object().shape({
    name: Yup.string().trim().required(),
    pageMarginOtherSettingId: Yup.number().nullable()
  })
})

const _PageMarginSettingUpsertForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  isValid,
  fetching,
  enumValues,
  isSettingWritable,
  title,
  onClickCancel,
  submitButtonText,
  alert,
  largestOrders,
  setting
}: {
  values: UpsertValues
  errors: Partial<Record<keyof UpsertValues, string>>
  touched: Partial<Record<keyof UpsertValues, boolean>>
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleBlur: React.FocusEventHandler<HTMLElement>
  handleSubmit: React.SubmitEventHandler<HTMLFormElement>
  setFieldValue: (field: string, value: unknown) => void
  isValid: boolean
  fetching: boolean
  enumValues: EnumValues
  isSettingWritable: boolean
  title: string
  onClickCancel: () => void
  submitButtonText: string
  alert: AlertState | null
  largestOrders: Record<string, number>
  setting: SettingProp | null | undefined
}) => {
  return (
    <>
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      {alert?.show && alert?.alertBody && (
        <Alert className='upsert-char-style-setting-errors-alert' variant='danger'>
          {alert.alertBody}
        </Alert>
      )}
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column className='col-3'>
              欄外項目種別
            </Form.Label>
            <Col>
              <Form.Control
                as='select'
                type='text'
                name='pageMarginSettingType'
                value={values.pageMarginSettingType || ''}
                onBlur={handleBlur}
                onChange={(e) => {
                  handleChange(e)
                  const pageMarginSettingType = e.target.value
                  const order = largestOrders[pageMarginSettingType] + 1
                  setFieldValue('order', order)
                  const pageMarginOtherSettingId =
                    pageMarginSettingType === 'other' && (setting?.pageMarginOtherSettings?.length ?? 0) > 0
                      ? (setting?.pageMarginOtherSettings?.at(0)?.id ?? '')
                      : ''
                  setFieldValue('pageMarginOtherSettingId', pageMarginOtherSettingId)
                }}
                isInvalid={!!(touched.pageMarginSettingType && errors.pageMarginSettingType)}
                disabled={!isSettingWritable}
              >
                {Object.keys(enumValues.pageMarginSettingTypes).map((k) => (
                  <option key={k} value={k} disabled={k === 'other' && setting?.pageMarginOtherSettings?.length === 0}>
                    {enumValues.pageMarginSettingTypes[k]}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type='invalid'>{errors.pageMarginSettingType}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          {values.pageMarginSettingType === 'other' && (
            <Form.Group as={Row} className='mb-3'>
              <Form.Label column className='col-3'>
                その他項目名
              </Form.Label>
              <Col>
                <Form.Control
                  as='select'
                  type='text'
                  name='pageMarginOtherSettingId'
                  value={values.pageMarginOtherSettingId ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!(touched.pageMarginOtherSettingId && errors.pageMarginOtherSettingId)}
                  disabled={!isSettingWritable}
                >
                  {setting?.pageMarginOtherSettings?.map((s) => (
                    <option key={s.name} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type='invalid'>{errors.pageMarginOtherSettingId}</Form.Control.Feedback>
              </Col>
            </Form.Group>
          )}
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column className='col-3'>
              欄外項目の名称
            </Form.Label>
            <Col>
              <Form.Control
                type='text'
                name='name'
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!(touched.name && errors.name)}
              />
              <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
            </Col>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClickCancel}>
            キャンセル
          </Button>
          <Button type='submit' disabled={!isValid || fetching}>
            {submitButtonText}
          </Button>
        </Modal.Footer>
      </Form>
    </>
  )
}

export const PageMarginSettingUpsertForm = Object.assign(_PageMarginSettingUpsertForm, {
  schema: Yup.object().shape({
    pageMarginSettingType: Yup.string().trim().required(),
    pageMarginOtherSettingId: Yup.number().when('pageMarginSettingType', {
      is: (pageMarginSettingType: string) => pageMarginSettingType === 'other',
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable()
    }),
    order: Yup.number().required().min(0),
    name: Yup.string().trim().required()
  })
})

export const PageMarginSettingDeleteForm = ({
  handleSubmit,
  title,
  submitButtonText,
  onClickCancel,
  selectedIds,
  pageMarginSettings,
  enumValues
}: {
  handleSubmit: React.SubmitEventHandler<HTMLFormElement>
  title: string
  submitButtonText: string
  onClickCancel: () => void
  selectedIds: number[]
  pageMarginSettings: DeleteItem[] | undefined
  enumValues: EnumValues
}) => {
  const selectedIdSet = new Set(selectedIds)
  const deleteStyles = pageMarginSettings?.filter((s) => selectedIdSet.has(s.id))
  return (
    <>
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body>
          <p>以下の欄外項目を削除します。よろしいですか？</p>
          <ListGroup>
            {deleteStyles?.map((style) => (
              <ListGroup.Item key={style.id}>
                id: {style.id}, 種別: {enumValues.pageMarginSettingTypes[camelCase(style.pageMarginSettingType)]}, 名称:{' '}
                {style.name}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClickCancel}>
            キャンセル
          </Button>
          <Button type='submit'>{submitButtonText}</Button>
        </Modal.Footer>
      </Form>
    </>
  )
}
